import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { ToggleButton } from "primereact/togglebutton";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { throttle } from "../../../utils/throttle";
import { useEffect, useState } from "react";
import { validateForm, hasErrors } from "../../../utils/validation";
import {
  buildTimeJson,
  inferScheduleType,
  parseHHmmToDate,
  applySingleWeek
} from "../../../utils/schedule";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import agent from "../../../agent";
import styles from "../style.module.scss";

const searchApi = throttle();

const initialForm = {
  // dictionaries
  city: null,             // {kato,name}
  streetType: null,       // id
  cargo: null,            // id (carrier)

  // address
  street: "",
  house: "",
  office: "",

  // blocks
  pickup: true,
  delivery: true,
  ptcDelivery: true,

  // dispatcher
  name: "",
  lastName: "",
  surname: "",
  phone: "+7",

  // storage
  storageMin: "",
  storageMax: "",

  // schedule
  scheduleType: null,     // "single" | "week"
  singleInterval: "",     // "HH:MM–HH:MM"
  weekTime: Array.from({ length: 7 }).map(() => [null, null]),
  comment: "",
  codeDPD: "",
};

const daysLabels = ["Пн.", "Вт.", "Ср.", "Чт.", "Пт.", "Сб.", "Вс."];

const DialogEditTerminal = ({
  viewDialog, hideDialog, cities, streetAbbrOptions,
  postalServices, editTerminal, showMessage, onSuccess,
}) => {
  const [cityOptions, setCityOptions] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!editTerminal) {
      setForm(initialForm);
      setErrors({});
      return;
    }

    const cityObject = cities
        ?.find((c) => c?.kato === editTerminal?.kato) || null;

    const weekTime = Array
        .from({ length: 7 })
        .map((_, idx) => {
      const day = editTerminal?.timeJson?.[idx];

      return [
        day?.start ? parseHHmmToDate(day.start) : null,
        day?.end ? parseHHmmToDate(day.end) : null,
      ];
    });

    const scheduleType = inferScheduleType(editTerminal?.timeJson);

    setForm((prev) => ({
      ...prev,
      city: cityObject,
      street: editTerminal?.street || "",
      house: editTerminal?.house || "",
      office: editTerminal?.office || "",
      streetType: editTerminal?.street_abbr_id || null,
      cargo: editTerminal?.postal_service_id || null,
      ptcDelivery: editTerminal?.is_term_ptc ?? true,
      pickup: editTerminal?.pickup ?? true,
      delivery: editTerminal?.delivery ?? true,
      name: editTerminal?.dispatcher?.firstName || "",
      lastName: editTerminal?.dispatcher?.lastName || "",
      surname: editTerminal?.dispatcher?.middleName || "",
      phone: editTerminal?.dispatcher?.phone || "+7",
      storageMin: "",
      storageMax: "",
      scheduleType,
      singleInterval: "",
      weekTime,
      comment: editTerminal?.message || "",
      codeDPD: editTerminal?.codeDPD || "",
    }));

    setErrors({});
  }, [editTerminal, cities]);

  const handle = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }

      const c = { ...prev };
      delete c[key];

      return c;
    });
  };

  const fetchCities = async (value) => {
    const data = await agent.Terminals.searchCity(value);
    setCityOptions(data);
  };

  const searchCity = (event) => {
    searchApi(async () => fetchCities(event.query), 250);
  };

  const handleWeekTime = (type, index, value) => {
    setForm((prev) => {
      const next = [...prev.weekTime];
      if (type === "clear") {
        next[index] = [null, null];
      }
      else if (type === "all") {
        // apply to all days
        const updated = next
            .map(() => [value.start, value.end]);

        return { ...prev, weekTime: updated };
      } else {
        next[index] = [
          type === "start" ? value : next[index][0],
          type === "end" ? value : next[index][1],
        ];
      }

      return { ...prev, weekTime: next };
    });
  };

  const closeDialog = () => {
    setForm(initialForm);
    setErrors({});
    hideDialog();
  };

  const buildPayload = () => {
    const payload = {
      city: form.city,
      streetType: form.streetType,
      street: form.street,
      house: form.house,
      office: form.office || null,
      carrier: form.cargo,
      blockSelfDropOff: !form.pickup,
      blockSelfPickup: !form.delivery,
      dispatcher: {
        firstName: form.name,
        lastName: form.lastName,
        middleName: form.surname || null,
        phone: form.phone,
      },
      scheduleType: form.scheduleType,
      singleInterval: form.scheduleType === "single" ? form.singleInterval : null,
      weekSchedule: form.scheduleType === "week" ? form.weekTime : null,
      storageMinDays: Number(form.storageMin),
      storageMaxDays: Number(form.storageMax),
      comment: form.comment || null,
    };

    if (form.scheduleType === "week") {
      payload.timeJson = buildTimeJson(form.weekTime);
    } else if (form.scheduleType === "single") {
      // parse "HH:MM–HH:MM" into dates for all days
      const parts = (form.singleInterval || "")
          .split(/–|-/)
          .map((x) => x.trim());
      const start = parts[0] ? parseHHmmToDate(parts[0]) : null;
      const end = parts[1] ? parseHHmmToDate(parts[1]) : null;
      const week = applySingleWeek(start, end);
      payload.timeJson = buildTimeJson(week);
    }

    return payload;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(
        form, { cities, streetAbbrOptions, postalServices }
    );
    setErrors(validationErrors);

    if (hasErrors(validationErrors)) {
      showMessage("error", "Ошибка", "Заполните все обязательные поля");
      return;
    }

    try {
      const payload = buildPayload();

      if (editTerminal) {
        await agent.Terminals.updateTerminal(editTerminal.id, payload);
        showMessage(
            "success",
            "Редактирование терминала/кросс-докинга",
            "Изменения успешно сохранены"
        );
      } else {
        await agent.Terminals.addTerminal(payload);
        showMessage(
            "success",
            "Создание терминала/кросс-докинга",
            "Новый терминал/кросс-докинг успешно создан"
        );
      }
      onSuccess();
    } catch (err) {
      closeDialog();
      showMessage("error", "Ошибка", `Операция не выполнена`);
    }
  };

  const dialogFooter = () => (
      <div>
        <Button label="Отменить"
                icon="pi pi-times"
                onClick={closeDialog}
                className="p-button-text"
        />
        <Button label="Сохранить"
                type="submit"
                icon="pi pi-check"
                onClick={handleSave}
                autoFocus
        />
      </div>
  );

  const isInvalid = (key) => !!errors?.[key];

  return (
      <Dialog
          header={editTerminal
              ? "Редактирование терминала ГП/Кросс-докинга"
              : "Добавление терминала ГП/Кросс-докинга"
          }
          visible={viewDialog}
          style={{ width: "70vw" }}
          footer={dialogFooter}
          onHide={closeDialog}
      >
        <div>
          <div className="p-fluid p-formgrid p-grid">
            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="city">Город</label>
              <AutoComplete
                  id="city"
                  className={`p-mt-1 ${isInvalid("city") ? "p-invalid" : ""}`}
                  value={form.city}
                  suggestions={cityOptions}
                  completeMethod={searchCity}
                  field="name"
                  dropdown
                  onChange={(e) => handle(
                      "city", e.value
                  )}
              />
              {isInvalid("city") && (
                  <small className="p-error">{errors.city}</small>
              )}
            </div>

            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="street">Улица</label>
              <InputText
                  id="street"
                  className={`p-mt-1 p-d-block p-mb-2 ${isInvalid("street") 
                      ? "p-invalid" 
                      : ""}`
                  }
                  placeholder="Название улицы"
                  value={form.street}
                  onChange={(e) => handle(
                      "street", e.target.value
                  )}
              />
              {isInvalid("street") && (
                  <small className="p-error">{errors.street}</small>
              )}
            </div>

            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="house">Дом</label>
              <InputText
                  id="house"
                  className={`p-mt-1 p-d-block p-mb-2 ${isInvalid("house") 
                      ? "p-invalid" 
                      : ""}`
                  }
                  placeholder="Номер дома"
                  value={form.house}
                  onChange={(e) => handle(
                      "house", e.target.value
                  )}
              />
              {isInvalid("house") && (
                  <small className="p-error">{errors.house}</small>
              )}
            </div>

            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="office">Офис</label>
              <InputText
                  style={{ width: "100%" }}
                  id="office"
                  className={`p-mt-1 p-d-block p-mb-2 ${isInvalid("office") 
                      ? "p-invalid" 
                      : ""}`
                  }
                  placeholder="Номер офиса"
                  value={form.office}
                  onChange={(e) => handle(
                      "office", e.target.value
                  )}
              />
              {isInvalid("office") && (
                  <small className="p-error">{errors.office}</small>
              )}
            </div>

            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="streetAbbr">Тип улицы</label>
              <Dropdown
                  inputId="streetAbbr"
                  value={form.streetType}
                  className={`p-mt-1 ${isInvalid("streetType") 
                      ? "p-invalid" 
                      : ""}`
                  }
                  options={streetAbbrOptions}
                  onChange={(e) => handle(
                      "streetType", e.value
                  )}
                  optionLabel="nameRu"
                  optionValue="id"
                  showClear
              />
              {isInvalid("streetType") && (
                  <small className="p-error">{errors.streetType}</small>
              )}
            </div>

            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="psId">Грузоперевозчик</label>
              <Dropdown
                  inputId="psId"
                  value={form.cargo}
                  className={`p-mt-1 ${isInvalid("cargo") ? "p-invalid" : ""}`}
                  options={postalServices}
                  onChange={(e) => handle("cargo", e.value)}
                  optionLabel="nameRu"
                  optionValue="id"
                  showClear
              />
              {isInvalid("cargo") && (
                  <small className="p-error">{errors.cargo}</small>
              )}
            </div>

            <div className="p-field p-col-12 p-md-12 p-d-flex p-flex-column">
              <div className="p-d-flex p-ai-strech p-mt-1" style={{ gap: "12px" }}>
                <ToggleButton
                    id="ptcDelivery"
                    checked={form.ptcDelivery}
                    onChange={(e) => handle(
                        "ptcDelivery", e.value
                    )}
                    onLabel="Да"
                    offLabel="Нет"
                    onIcon="pi pi-check"
                    offIcon="pi pi-times"
                    tooltip="Данный терминал выбран для забора заказов PTC"
                    tooltipOptions={{ position: "bottom" }}
                    style={{ width: "9rem" }}
                />

                <div className="p-d-flex p-ai-center" style={{ gap: "4px" }}>
                  <Checkbox
                      inputId="pickup"
                      name="pickup"
                      onChange={(e) => handle(
                          "pickup", !e.checked
                      )}
                      checked={!form.pickup}
                      binary
                  />
                  <label htmlFor="pickup" className="ml-2">
                    Блокировать самопривоз
                  </label>
                </div>

                <div className="p-d-flex p-ai-center" style={{ gap: "4px" }}>
                  <Checkbox
                      inputId="delivery"
                      name="delivery"
                      onChange={(e) => handle(
                          "delivery", !e.checked
                      )}
                      checked={!form.delivery}
                      binary
                  />
                  <label htmlFor="delivery" className="ml-2">
                    Блокировать самовывоз
                  </label>
                </div>
              </div>
            </div>
          </div>

          <h4>Диспетчер терминала/кросс-докинга SLQ</h4>
          <div className="p-fluid p-formgrid p-grid">
            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="name">Имя</label>
              <InputText
                  id="name"
                  className={`p-mt-1 p-d-block p-mb-2 ${isInvalid("name") 
                      ? "p-invalid" 
                      : ""}`
                  }
                  placeholder="Введите имя"
                  value={form.name}
                  onChange={(e) => handle(
                      "name", e.target.value
                  )}
              />
              {isInvalid("name") && (
                  <small className="p-error">{errors.name}</small>
              )}
            </div>

            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="lastName">Фамилия</label>
              <InputText
                  id="lastName"
                  className={`p-mt-1 p-d-block p-mb-2 ${isInvalid("lastName") 
                      ? "p-invalid" 
                      : ""}`
                  }
                  placeholder="Введите фамилию"
                  value={form.lastName}
                  onChange={(e) => handle(
                      "lastName", e.target.value
                  )}
              />
              {isInvalid("lastName") && (
                  <small className="p-error">{errors.lastName}</small>
              )}
            </div>

            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="surname">Отчество</label>
              <InputText
                  id="surname"
                  className={`p-mt-1 p-d-block p-mb-2 ${isInvalid("surname") 
                      ? "p-invalid" 
                      : ""}`
                  }
                  placeholder="Введите отчество"
                  value={form.surname}
                  onChange={(e) => handle(
                      "surname", e.target.value
                  )}
              />
              {isInvalid("surname") && (
                  <small className="p-error">{errors.surname}</small>
              )}
            </div>

            <div className="p-field p-col-12 p-md-4">
              <label htmlFor="phone">Номер телефона</label>
              <InputText
                  id="phone"
                  className={`p-mt-1 p-d-block p-mb-2 ${isInvalid("phone") 
                      ? "p-invalid" 
                      : ""}`
                  }
                  value={form.phone}
                  onChange={(e) => handle(
                      "phone", e.target.value
                  )}
                  placeholder="+7XXXXXXXXXX"
              />
              {isInvalid("phone") && (
                  <small className="p-error">{errors.phone}</small>
              )}
            </div>
          </div>

          <div className="p-fluid p-formgrid p-grid">
            <div className="p-field p-col-12 p-md-6">
              <label>Срок хранения (мин.)</label>
              <InputNumber
                  value={form.storageMin === "" ? null : Number(form.storageMin)}
                  onValueChange={(e) => handle(
                      "storageMin", e.value === null ? "" : String(e.value)
                  )}
                  useGrouping={false}
                  className={`${isInvalid("storageMin") ? "p-invalid" : ""}`}
                  placeholder="Введите мин.количество дней"
              />
              {isInvalid("storageMin") && (
                  <small className="p-error">{errors.storageMin}</small>
              )}
            </div>
            <div className="p-field p-col-12 p-md-6">
              <label>Срок хранения (макс.)</label>
              <InputNumber
                  value={form.storageMax === "" ? null : Number(form.storageMax)}
                  onValueChange={(e) => handle(
                      "storageMax", e.value === null ? "" : String(e.value)
                  )}
                  useGrouping={false}
                  className={`${isInvalid("storageMax") ? "p-invalid" : ""}`}
                  placeholder="Введите макс.количество дней"
              />
              {isInvalid("storageMax") && (
                  <small className="p-error">{errors.storageMax}</small>
              )}
            </div>
          </div>

          <h4>Время работы терминала/кросс-докинга</h4>

          <div className="p-field p-col-12 p-md-12 p-d-flex p-ai-center"
               style={{ gap: "16px" }}
          >
            <b>Тип расписания</b>

            <div className="p-d-flex p-ai-center" style={{ gap: "8px" }}>
              <Checkbox
                  inputId="schedule_single"
                  checked={form.scheduleType === "single"}
                  onChange={() => handle("scheduleType", "single")}
                  binary
              />
              <label htmlFor="schedule_single">Единый график</label>
            </div>

            <div className="p-d-flex p-ai-center" style={{ gap: "8px" }}>
              <Checkbox
                  inputId="schedule_week"
                  checked={form.scheduleType === "week"}
                  onChange={() => handle("scheduleType", "week")}
                  binary
              />
              <label htmlFor="schedule_week">График по дням</label>
            </div>
          </div>

          {isInvalid("scheduleType") && (
              <small className="p-error">{errors.scheduleType}</small>
          )}

          {form.scheduleType === "single" && (
              <div className="p-fluid p-formgrid p-grid p-ml-2">
                <div className="p-field p-col-12 p-md-5" style={{ width: "14rem" }}>
                  <p><b>Единый график (ЧЧ:ММ–ЧЧ:ММ):</b></p>
                </div>
                <div className="p-field p-col-12 p-md-6">
                  <InputText
                      value={form.singleInterval}
                      onChange={(e) => handle(
                          "singleInterval", e.target.value
                      )}
                      className={`${isInvalid("singleInterval") 
                          ? "p-invalid" 
                          : ""}`
                      }
                      placeholder="09:00–18:00"
                  />
                  {isInvalid("singleInterval") && (
                      <small className="p-error">{errors.singleInterval}</small>
                  )}
                </div>
              </div>
          )}

          {form.scheduleType === "week" && (
              <div className={`${styles.scheduleGrid} p-fluid p-formgrid p-grid`}>
                <div className="p-field p-col-6">
                  <div className="p-fluid p-formgrid p-grid">
                    <div className="p-field p-col-12">
                      <p><b>Установить для всех дней:</b></p>
                    </div>
                    <div className="p-field p-col-1"></div>
                    <div className="p-field p-col-4">
                      <Calendar
                          id="time-start-all"
                          value={null}
                          onChange={(e) => handleWeekTime(
                              "all",
                              0,
                              {
                                start: e.value,
                                end: form.weekTime?.[0]?.[1] || null
                              }
                          )}
                          timeOnly
                      />
                    </div>
                    <p className="p-field p-col-2 p-text-center">—</p>
                    <div className="p-field p-col-4">
                      <Calendar
                          id="time-end-all"
                          value={null}
                          onChange={(e) => handleWeekTime(
                              "all",
                              0,
                              {
                                start: form.weekTime?.[0]?.[0] || null,
                                end: e.value
                              }
                          )}
                          timeOnly
                      />
                    </div>
                    <Button
                        onClick={() => handleWeekTime("clear", id)}
                        className="p-col-1 p-button-text p-button-rounded"
                        icon="pi pi-times"
                    />
                  </div>

                  {daysLabels.slice(0, 4).map((day, id) => (
                      <div key={id} className="p-fluid p-formgrid p-grid">
                        <div className="p-field p-col-1">
                          <p><b>{day}:</b></p>
                        </div>
                        <div className="p-field p-col-4">
                          <Calendar
                              id={`time-start-${id}`}
                              value={form.weekTime[id][0]}
                              onChange={(e) => handleWeekTime(
                                  "start", id, e.value
                              )}
                              timeOnly
                              className={`${errors.weekTime?.[id] 
                                  ? "p-invalid" 
                                  : ""}`
                              }
                          />
                        </div>
                        <p className="p-field p-col-2 p-text-center">—</p>
                        <div className="p-field p-col-4">
                          <Calendar
                              id={`time-end-${id}`}
                              value={form.weekTime[id][1]}
                              onChange={(e) => handleWeekTime(
                                  "end", id, e.value)
                              }
                              timeOnly
                              className={`${errors.weekTime?.[id] 
                                  ? "p-invalid" 
                                  : ""}`
                              }
                          />
                        </div>
                        <Button
                            onClick={() => handleWeekTime("clear", id)}
                            className="p-col-1 p-button-text p-button-rounded"
                            icon="pi pi-times"
                        />
                        {errors.weekTime?.[id] && (
                            <small className="p-error">{errors.weekTime[id]}</small>
                        )}
                      </div>
                  ))}
                </div>

                <div className="p-field p-col-6">
                  {[null, null].map((_, id) => (
                      <div key={id}
                           className={`${styles.hiddenGridElement} p-fluid p-formgrid p-grid`}
                      >
                        <div className="p-field p-col-1"></div>
                        <div className="p-field p-col-4">
                          <Calendar id="time-start-all" timeOnly/>
                        </div>
                        <p className="p-field p-col-2 p-text-center">—</p>
                        <div className="p-field p-col-4">
                          <Calendar id="time-end-all" timeOnly/>
                        </div>
                        <Button
                            onClick={() => handleWeekTime("clear", id)}
                            className="p-col-1 p-button-text p-button-rounded"
                            icon="pi pi-times"
                        />
                      </div>
                  ))}

                  {daysLabels.slice(4).map((day, id) => {
                    const currentId = id + 4;

                    return (
                        <div key={currentId} className="p-fluid p-formgrid p-grid">
                          <div className="p-field p-col-1">
                            <p><b>{day}:</b></p>
                          </div>
                          <div className="p-field p-col-4">
                            <Calendar
                                id={`time-start-${currentId}`}
                                value={form.weekTime[currentId][0]}
                                onChange={(e) => handleWeekTime(
                                    "start", currentId, e.value
                                )}
                                timeOnly
                                className={`${errors.weekTime?.[currentId] 
                                    ? "p-invalid" 
                                    : ""}`
                                }
                            />
                          </div>
                          <p className="p-field p-col-2 p-text-center">—</p>
                          <div className="p-field p-col-4">
                            <Calendar
                                id={`time-end-${currentId}`}
                                value={form.weekTime[currentId][1]}
                                onChange={(e) => handleWeekTime(
                                    "end", currentId, e.value
                                )}
                                timeOnly
                                className={`${errors.weekTime?.[currentId] 
                                    ? "p-invalid" 
                                    : ""}`
                                }
                            />
                          </div>
                          <Button
                              onClick={() => handleWeekTime("clear", currentId)}
                              className="p-col-1 p-button-text p-button-rounded"
                              icon="pi pi-times"
                          />
                          {errors.weekTime?.[currentId] && (
                              <small className="p-error">{errors.weekTime[currentId]}</small>
                          )}
                        </div>
                    );
                  })}
                </div>
              </div>
          )}

          <h4>Дополнительный комментарий</h4>
          <div className="p-fluid p-formgrid p-grid">
            <div className="p-field p-col-12 p-md-12">
              <InputTextarea
                  className={`p-mt-1 ${isInvalid("comment") ? "p-invalid" : ""}`}
                  style={{ width: "100%" }}
                  value={form.comment}
                  onChange={(e) => handle(
                      "comment", e.target.value
                  )}
                  rows={1}
                  cols={50}
                  autoResize
              />
              {isInvalid("comment") && (
                  <small className="p-error">{errors.comment}</small>
              )}
            </div>
          </div>
        </div>
      </Dialog>
  );
};

export default DialogEditTerminal;
